import React, { Children, useEffect, useRef, useState } from 'react';

import { Button } from '@tecma/ds';
import classNames from 'classnames';
import { register, SwiperContainer } from 'swiper/element/bundle';

import type { SwiperController } from 'hooks/useSwiperController';
import type { SwiperSlideProps, SwiperProps } from 'swiper/react';


import './swiper-carousel.scss';

interface SwiperCarouselRequiredProps {
  items: React.ReactNode[];
}

// Optional Props
interface SwiperCarouselOptionalProps {
  className?: string;
  swiperProps?: SwiperProps;
  swiperSlideProps?: SwiperSlideProps;
  hideArrowButtons?: boolean;
  swiperController?: SwiperController;
}

// Combined required and optional props to build the full prop interface
export interface SwiperCarouselProps extends SwiperCarouselRequiredProps, SwiperCarouselOptionalProps {}

const SwiperCarousel: React.FC<SwiperCarouselProps> = ({
  items,
  swiperProps,
  swiperSlideProps,
  className,
  hideArrowButtons,
  swiperController,
}) => {
  const swiperCarouselClassList = classNames('swiper-carousel', {
    [`${className}`]: className,
  });
  const defaultSwiperProps: SwiperProps = {
    slidesPerView: 1,
    allowTouchMove: false,
  };
  const props: SwiperProps = {
    ...defaultSwiperProps,
    ...swiperProps,
  };

  return (
    <div className={swiperCarouselClassList}>
      <SwiperComponent swiperProps={props} hideArrowButtons={hideArrowButtons} swiperController={swiperController}>
        {items.map((item) => (
          <SwiperSlide {...swiperSlideProps}>{item}</SwiperSlide>
        ))}
      </SwiperComponent>
    </div>
  );
};

interface SwiperRequiredProps {
  swiperProps: SwiperProps;
}

// Optional Props
interface SwiperOptionalProps {
  children?: React.ReactNode;
  hideArrowButtons?: boolean;
  swiperController?: SwiperController;
}

// Combined required and optional props to build the full prop interface
export interface SwiperComponentProps extends SwiperRequiredProps, SwiperOptionalProps {}

export const SwiperComponent: React.FC<SwiperComponentProps> = ({ swiperProps, hideArrowButtons, children, swiperController }) => {
  const swiperRef = useRef<SwiperContainer>(null);
  const paginationRef = useRef(null);
  const [showNextButton, setShowNextButton] = useState(false);
  const [showPrevButton, setShowPrevButton] = useState(false);
  const swiperContainerClassList = classNames('swiper-container', {
    'left-overflow': Children.count(children) > 0 && showPrevButton,
    'right-overflow': Children.count(children) > 0 && showNextButton,
  });

  const handleNextClick = () => {
    swiperRef.current?.swiper.slideNext();
    if (swiperController?.handleNextClick) {
      swiperController.handleNextClick();
    }
    calcShowButton();
  };
  const handlePrevClick = () => {
    if (swiperController?.handlePrevClick) {
      swiperController.handlePrevClick();
    }
    swiperRef.current?.swiper.slidePrev();
    calcShowButton();
  };
  const calcShowButton = () => {
    if (swiperRef.current?.swiper && !swiperProps.loop) {
      const showNext = !swiperRef.current?.swiper.isEnd;
      const showPrev = !swiperRef.current?.swiper.isBeginning;
      if (showNextButton !== showNext) {
        setShowNextButton(showNext);
      }
      if (showPrevButton !== showPrev) {
        setShowPrevButton(showPrev);
      }
    } else {
      setShowNextButton(true);
      setShowPrevButton(true);
    }
  };

  useEffect(() => {
    if (
      swiperController?.activeIndex != null &&
      swiperRef.current?.swiper != null &&
      swiperController.activeIndex !== swiperRef.current.swiper.activeIndex
    ) {
      swiperRef.current?.swiper?.slideTo(swiperController.activeIndex);
    }
  }, [swiperController?.activeIndex]);

  useEffect(() => {
    calcShowButton();
  }, [swiperRef.current?.swiper.isEnd, swiperRef.current?.swiper.isBeginning]);

  useEffect(() => {
    register();
    const params = {
      ...swiperProps,
      className: swiperContainerClassList,
    };
    if (swiperRef.current) {
      Object.assign(swiperRef.current, params);
      swiperRef.current.initialize();
      calcShowButton();
    }
  }, [swiperProps, showNextButton, showPrevButton]);

  return (
    <>
      <swiper-container init={false} ref={swiperRef}>
        {children}
      </swiper-container>

      {!hideArrowButtons && (
        <div slot='container-end' ref={paginationRef} className='buttons-container'>
          {showPrevButton && (
            <Button
              onClick={handlePrevClick as React.MouseEventHandler<HTMLButtonElement>}
              iconName='chevron-left'
              className='prev'
              color='inverse'
              size='small'
            />
          )}
          {showNextButton && (
            <Button
              onClick={handleNextClick as React.MouseEventHandler<HTMLButtonElement>}
              iconName='chevron-right'
              className='next'
              color='inverse'
              size='small'
            />
          )}
        </div>
      )}
    </>
  );
};

export function SwiperSlide(props: SwiperSlideProps) {
  const { children, ...rest } = props;
  return <swiper-slide {...rest}>{children}</swiper-slide>;
}

export default SwiperCarousel;
